import {
  provide,
  inject,
  ref,
  onBeforeMount,
  onBeforeUnmount,
  type InjectionKey
} from 'vue'
import { emitter } from '@/plugins/emitter'
import { moveHistoryListItem } from '@/utils/format-edit/historyList'
import type {
  ShokurekishoFormatData,
  ShokurekishoJobHistoryItem,
  ShokurekishoProjectItem,
  ShokurekishoSkillItem
} from '@/composables/format-edit/useShokurekishoFormatData'

function createUuid(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

type SkillColumns = Partial<Omit<ShokurekishoSkillItem, 'uuid'>>
type JobColumns = Partial<
  Omit<ShokurekishoJobHistoryItem, 'uuid' | 'projects'>
> & {
  projects?: ShokurekishoProjectItem[]
}
type ProjectColumns = Partial<Omit<ShokurekishoProjectItem, 'uuid'>>

function createSkillItem(columns: SkillColumns = {}): ShokurekishoSkillItem {
  return {
    skillname: columns.skillname || '',
    years: Number(columns.years) || '',
    description: columns.description || '',
    uuid: createUuid()
  }
}

function createJobHistoryItem(
  columns: JobColumns = {}
): ShokurekishoJobHistoryItem {
  return {
    companyName: columns.companyName || '',
    startYear: Number(columns.startYear) || '',
    startMonth: Number(columns.startMonth) || '',
    endYear: Number(columns.endYear) || '',
    endMonth: Number(columns.endMonth) || '',
    isContinuing: columns.isContinuing || false,
    capitalStock: columns.capitalStock || '',
    numberOfEmployees: columns.numberOfEmployees || '',
    businessArea: columns.businessArea || '',
    projects: columns.projects || [],
    hopeJobcategory: columns.hopeJobcategory || '',
    totalYear: Number(columns.totalYear) || '',
    totalMonth: Number(columns.totalMonth) || '',
    uuid: createUuid()
  }
}

function createProjectItem(
  columns: ProjectColumns = {}
): ShokurekishoProjectItem {
  return {
    projectTitle: columns.projectTitle || '',
    startYear: Number(columns.startYear) || '',
    startMonth: Number(columns.startMonth) || '',
    endYear: Number(columns.endYear) || '',
    endMonth: Number(columns.endMonth) || '',
    contentsOfProject: columns.contentsOfProject || '',
    envAndScale: columns.envAndScale || '',
    position: columns.position || '',
    businessArea: columns.businessArea || '',
    capitalStock: columns.capitalStock || '',
    numberOfEmployees: columns.numberOfEmployees || '',
    dev: columns.dev || '',
    numberOfTeam: columns.numberOfTeam || '',
    phase1: columns.phase1 || false,
    phase2: columns.phase2 || false,
    phase3: columns.phase3 || false,
    phase4: columns.phase4 || false,
    phase5: columns.phase5 || false,
    phase6: columns.phase6 || false,
    uuid: createUuid()
  }
}

export type ShokurekishoHistories = ReturnType<typeof useShokurekishoHistories>

const SHOKUREKISHO_HISTORIES_KEY: InjectionKey<ShokurekishoHistories> = Symbol(
  'shokurekishoHistories'
)

export function useShokurekishoHistories(formatData: ShokurekishoFormatData) {
  const activeHistoryItem = ref('')

  function addSkillHistory(index?: number | null, columns?: SkillColumns) {
    const list = formatData.skillsToUse
    const item = createSkillItem(columns)
    if (typeof index === 'number') {
      list.splice(index, 0, item)
    } else {
      list.push(item)
    }
  }

  function deleteSkillHistory(index: number) {
    formatData.skillsToUse.splice(index, 1)
  }

  function moveSkillHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.skillsToUse, params)
  }

  function addJobHistory(index?: number | null, columns?: JobColumns) {
    const list = formatData.jobHistories
    const item = createJobHistoryItem(columns)
    if (typeof index === 'number') {
      list.splice(index, 0, item)
    } else {
      list.push(item)
    }
  }

  function deleteJobHistory(index: number) {
    formatData.jobHistories.splice(index, 1)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.jobHistories, params)
  }

  function addProjectHistory(index: number, columns?: ProjectColumns) {
    const target = formatData.jobHistories[index]
    if (!target) return
    if (!target.projects) target.projects = []
    target.projects.push(createProjectItem(columns))
  }

  function deleteHistoryDetail(index: number, index2: number) {
    const target = formatData.jobHistories[index]
    if (!target || !target.projects) return
    target.projects.splice(index2, 1)
  }

  function moveJobHistoryDetail(
    params: { index: number; to: string },
    parentIndex: number
  ) {
    const target = formatData.jobHistories[parentIndex]
    if (!target || !target.projects) return
    moveHistoryListItem(target.projects, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  onBeforeMount(() => {
    emitter.on('deactivate-history-item', deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off('deactivate-history-item', deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    addSkillHistory,
    deleteSkillHistory,
    moveSkillHistory,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    addProjectHistory,
    deleteHistoryDetail,
    moveJobHistoryDetail,
    activateHistoryItem,
    deactivateHistoryItem
  }
}

export function provideShokurekishoHistories(histories: ShokurekishoHistories) {
  provide(SHOKUREKISHO_HISTORIES_KEY, histories)
}

export function useProvidedShokurekishoHistories(): ShokurekishoHistories {
  const histories = inject(SHOKUREKISHO_HISTORIES_KEY)
  if (!histories) {
    throw new Error(
      'useProvidedShokurekishoHistories() must be used under ShokurekishoEdit'
    )
  }
  return histories
}
